import { Box, Icon, Text, Tooltip, useToggle } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import UserAvatar from 'modules/core/components/UserAvatar'
import { employeeLegalEntityId } from 'modules/core/lib/employeeLegalEntity'
import formatCurrency from 'modules/core/lib/formatCurrency'
import { currencyToNumber } from 'modules/core/lib/formatCurrencyUtils'
import i18n from 'modules/core/lib/i18n'
import users from 'modules/core/store/collections/users'

import type Employee from 'modules/employees/store/models/Employee'
import { EmployeeEstimatedPeriodBaseSalary } from 'modules/payroll_bundle/hooks/useEmployeeEstimatedPeriodBaseSalary'

const employeePeriodBaseSalary = (
  estimatedPeriodBaseSalary: number | null | undefined,
  baseSalary: number | null | undefined,
  baseSalaryPeriodicity: string | null | undefined,
  employeeLegalEntityId: number | undefined
) => {
  if (estimatedPeriodBaseSalary) {
    return i18n.t('handoffs.supplements.salary_this_period', {
      salary: formatCurrency(
        currencyToNumber(estimatedPeriodBaseSalary),
        undefined,
        null,
        employeeLegalEntityId
      ),
    })
  }
  if (baseSalary) {
    return `${formatCurrency(
      currencyToNumber(baseSalary),
      undefined,
      null,
      employeeLegalEntityId
    )}/${baseSalaryPeriodicity}`
  }
  return i18n.t('handoffs.supplements.salary_not_informed')
}

interface Props {
  employee: Employee
  estimatedSalary?: EmployeeEstimatedPeriodBaseSalary
  observationAvailable?: boolean
  observationText?: string
  hover: boolean
  openModal: () => void
}

const EmployeeHeader = ({
  employee,
  estimatedSalary,
  observationAvailable,
  observationText,
  hover,
  openModal,
}: Props) => {
  const [open, toggle] = useToggle(false)

  const employeeEstimatedBaseSalary = estimatedSalary

  // Lighten the name if the employee will be terminated
  const nameColor = !employee.isTerminated && employee.terminationDate ? 'grey600' : 'grey1000'

  return (
    <Box flexDirection="row">
      <Box paddingRight="s16" justifyContent="center">
        <UserAvatar user={users.get(employee.attributes.access_id)} size="s40" />
      </Box>
      <Box flexDirection="column" justifyContent="center">
        <Box flexDirection="row" alignItems="center" gap="s4">
          <Text.Normal
            weight="medium"
            color={nameColor}
            stroked={employee.isTerminated}
            overflowWrap
          >
            {users.get(employee.attributes.access_id)?.legalName}
          </Text.Normal>

          {!hover && observationAvailable && (
            <Box aria-label="employee note">
              <Icon.Small icon="comment" color="teal1000" />
            </Box>
          )}

          {hover && observationAvailable && (
            <Box aria-label="employee note">
              <Tooltip.ItemList
                key={employee.attributes.id}
                open={open}
                toggle={toggle}
                attachment="bottom left"
              >
                <Box onClick={openModal}>
                  <Icon.Small icon="comment" color="teal1000" />
                </Box>

                <Box textAlign="left">
                  <Text.Normal size="200" lineClamp={4} overflowWrap>
                    {observationText}
                  </Text.Normal>
                </Box>
              </Tooltip.ItemList>
            </Box>
          )}
        </Box>
        <Box flexDirection="row" gap="s4">
          <Text.Normal size="200" weight="normal">
            {employeePeriodBaseSalary(
              employeeEstimatedBaseSalary?.periodBaseSalaryAmount,
              employeeEstimatedBaseSalary?.baseSalaryAmount,
              employeeEstimatedBaseSalary?.baseSalaryPeriodicity,
              employeeLegalEntityId(employee)
            )}
          </Text.Normal>
        </Box>
      </Box>
    </Box>
  )
}

export default observer(EmployeeHeader)
